import clsx from 'clsx';
import { useRef, useState, type ReactNode, type UIEvent } from 'react';
import { MdArrowDropDown, MdCheck, MdClose } from 'react-icons/md';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

const SCROLL_END_THRESHOLD_PX = 24;
const DIALOG_COLLISION_PADDING_PX = 24;

export type MailRecipientsDropdownItem = {
  key: string | number;
  label: ReactNode;
  description?: ReactNode;
  onRemove?: () => void;
  selected?: boolean;
  onSelect?: () => void;
};

type MailRecipientsDropdownProps = {
  items: MailRecipientsDropdownItem[];
  disabled?: boolean;
  onOpenChange?: (open: boolean) => void;
  onReachEnd?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  contentWidthClassName?: string;
};

export default function MailRecipientsDropdown({
  items,
  disabled,
  onOpenChange,
  onReachEnd,
  hasMore = false,
  isLoadingMore = false,
  contentWidthClassName = 'w-[320px]',
}: MailRecipientsDropdownProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [collisionBoundary, setCollisionBoundary] = useState<Element | null>(null);

  const handleOpenChange = (open: boolean) => {
    if (open) setCollisionBoundary(triggerRef.current?.closest('[role="dialog"]') ?? null);
    onOpenChange?.(open);
  };

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    if (scrollTop + clientHeight >= scrollHeight - SCROLL_END_THRESHOLD_PX) onReachEnd?.();
  };

  return (
    <Popover onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          aria-label="送信先を全て表示"
          disabled={disabled || (items.length === 0 && !onOpenChange)}
          className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-sm border-none bg-transparent p-0 text-main transition-colors hover:bg-gray disabled:cursor-not-allowed disabled:opacity-50 data-[state=open]:bg-gray_darken"
        >
          <MdArrowDropDown className="size-5" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        collisionBoundary={collisionBoundary}
        collisionPadding={collisionBoundary ? DIALOG_COLLISION_PADDING_PX : 0}
        onScroll={onReachEnd ? handleScroll : undefined}
        className={clsx(
          'max-h-[320px] overflow-y-auto rounded-sm px-md py-sm shadow-layer3!',
          contentWidthClassName
        )}
      >
        <ul className="flex flex-col">
          {items.map((item) => (
            <li key={item.key} className="group flex items-start gap-xs py-[2px]">
              {item.onSelect ? (
                <button
                  type="button"
                  aria-pressed={item.selected}
                  onClick={item.onSelect}
                  className="flex min-w-0 flex-1 cursor-pointer items-start gap-xs border-none bg-transparent p-0 text-left text-body-sm break-words text-main"
                >
                  <span className="min-w-0 flex-1">{item.label}</span>
                  {item.selected && <MdCheck className="size-icon-xxs shrink-0 text-primary" />}
                </button>
              ) : (
                <div className="min-w-0 flex-1 text-body-sm break-words text-main">
                  {item.label}
                  {item.description && <div className="text-body-xs">{item.description}</div>}
                </div>
              )}
              {item.onRemove && (
                <button
                  type="button"
                  aria-label="送信先から外す"
                  onClick={item.onRemove}
                  className="flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-sm border-none bg-transparent p-0 text-sub opacity-0 transition group-hover:opacity-100 hover:bg-gray hover:text-main focus-visible:opacity-100"
                >
                  <MdClose className="size-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
        {hasMore && (
          <button
            type="button"
            onClick={onReachEnd}
            disabled={isLoadingMore}
            className="mt-xs w-full cursor-pointer rounded-sm border-none bg-transparent py-xxs text-button-sm text-primary hover:bg-primary_lighten disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isLoadingMore ? '読み込み中...' : 'さらに読み込む'}
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}
